import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Card, Button, Tooltip, Alert } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import ComponentHeader from 'app/shared/ui/ComponentHeader';
import NewDiscountActionWizard from 'app/pages/DiscountEditor/components/NewDiscountActionWizard';
import DeleteDiscountActionConfirmationModal from 'app/pages/DiscountEditor/components/DeleteDiscountActionConfirmationModal';
import EditDiscountActionDialog from 'app/pages/DiscountEditor/components/EditDiscountActionDialog/EditDiscountActionDialog';
import { Action } from 'app/pages/DiscountEditor/actionTypes';
import {
  showAddDiscountActionWizard,
  showDeleteActionConfirmation,
  startEditingDiscountAction,
} from 'app/pages/DiscountEditor/actions';
import { getSelectedSchools, getSelectedCurrencies, getProductByCode as selectProductByCode } from 'app/pages/DiscountEditor/modules/ProductSelection';
import { getSelectedDivisionIds } from 'app/pages/DiscountEditor/modules/Divisions/selectors';
import { getSavedDiscountActions, SavedDiscountAction, getFees } from 'app/pages/DiscountEditor/modules/DiscountActions';
import { Currency } from 'app/shared/model';
import { Fee } from 'app/shared/model/Fee';
import { DiscountActionDescription } from 'app/pages/DiscountEditor/components/DiscountActionDescription';
import ErrorSummary from 'app/pages/DiscountEditor/components/ErrorSummary';
import { getSectionError } from 'app/pages/DiscountEditor/modules/Editor';
import { SaveDiscountErrorCategory } from 'app/shared/model/SaveDiscountResultDto';
import { getAllCurrencies } from 'app/pages/DiscountEditor/modules/RefData';

// Props from Redux state
interface StateProps {
  canEditActions: boolean;
  currency?: Currency;
  actions: SavedDiscountAction[];
  errorMessage?: string;
  getProductByCode: (code: string) => any;
  getFeeByCode: (code: string) => Fee | undefined;
  allCurrencies: Currency[];
}

// Dispatch props
interface DispatchProps {
  addAction: () => void;
  editAction: (action: SavedDiscountAction) => void;
  showDeleteConfirmation: (action: SavedDiscountAction) => void;
}

type Props = StateProps & DispatchProps;

const DiscountActionTable: React.FC<Props> = props => (
  <div style={{ marginBottom: 16, background: '#fff', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
    <ComponentHeader text="Actions" />
    <div style={{ padding: '0 16px 16px' }}>
      <ErrorSummary errorMessage={props.errorMessage} />

      <div style={{ marginBottom: 16 }}>
    {/* No-actions message */}
    {props.actions.length === 0 && (
      <Alert
        type="info"
        title="Action Required"
        description="Please ensure that you add at least one action before saving the discount."
        showIcon
        style={{ marginBottom: 16 }}
      />
    )}

    {/* List of actions */}
    {props.actions.map((action, index) => {
      const actionCurrency =
          action.currencyCode
              ? props.allCurrencies.find(c => (c as any).code === action.currencyCode || (c as any).isoCode === action.currencyCode)
              : undefined;

      return (
        <div
          key={action.id}
          style={{
            padding: index === 0 ? '0 0 16px' : '16px 0',
            borderTop: index === 0 ? 'none' : '1px solid #f0f0f0',
          }}
        >
          <DiscountActionDescription
            action={action}
            currency={actionCurrency}
            getProductByCode={props.getProductByCode}
            getFeeByCode={props.getFeeByCode}
            showDeleteConfirmation={props.showDeleteConfirmation}
            editAction={props.editAction}
          />
        </div>
      );
    })}
      </div>

    <div style={{ marginTop: 16, textAlign: 'center' }}>
      <Tooltip title={!props.canEditActions ? 'Please select Division and at least one School' : ''}>
        <span>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={props.addAction}
            disabled={!props.canEditActions}
          >
            Add Action
          </Button>
        </span>
      </Tooltip>
    </div>

    <NewDiscountActionWizard />
    <DeleteDiscountActionConfirmationModal />
    <EditDiscountActionDialog />
    </div>
  </div>
);

const mapStateToProps = (state: any): StateProps => {
  const schools = getSelectedSchools(state);
  const selectedDivisionIds = getSelectedDivisionIds(state);
  const hasDivision = selectedDivisionIds.length > 0;
  const hasSchools = Object.keys(schools).length > 0;
  const fees = getFees(state);
  const allCurrencies = getAllCurrencies(state) ?? [];
  return {
    errorMessage: getSectionError(state, SaveDiscountErrorCategory.Action),
    canEditActions: hasDivision && hasSchools,
    actions: getSavedDiscountActions(state),
    allCurrencies,
    getProductByCode: code => selectProductByCode(state, code),
    getFeeByCode: code => {
      const normalised = String(code).replace(/^F/i, ''); 
      return fees.find(f => f.name === normalised || String(f.id) === normalised);
      },
  };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => ({
  addAction: () => dispatch(showAddDiscountActionWizard()),
  editAction: action => dispatch(startEditingDiscountAction(action)),
  showDeleteConfirmation: action => dispatch(showDeleteActionConfirmation(action)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DiscountActionTable);
